import React, { useEffect, useState } from 'react'
import styles from '../css/EditTargetV2.module.css'
import { TYPE_ORDER } from '../targetingUtils'

function TypeChip({ value, size = 'md' }) {
    if (!value || value === 'NT') return <span className={styles.dash}>—</span>
    return <span className={[styles.chip, styles[`chip${value}`], styles[`chip_${size}`]].join(' ')}>{value}</span>
}

function directionLabel(from, to) {
    if (!to || to === 'NT') return { word: 'dropping', badge: 'demoted' }
    if (!from || from === 'NT') return { word: 'promoting', badge: 'promoted' }
    const i1 = TYPE_ORDER.indexOf(from)
    const i2 = TYPE_ORDER.indexOf(to)
    if (i2 < i1) return { word: 'promoting', badge: 'promoted' }
    if (i2 > i1) return { word: 'demoting',  badge: 'demoted'  }
    return        { word: 'changing',   badge: 'changed'  }
}

export default function EditTargetV2({ row, targetType, exercise, options, reason: initialReason, notes: initialNotes, lock, dirMode, motMode, onSubmit, onUndo, onUndoMOT, onClose }) {
    const [reason, setReason]           = useState(initialReason || '')
    const [notes, setNotes]             = useState(initialNotes  || '')
    const [reasonError, setReasonError] = useState(false)
    const [notesError, setNotesError]   = useState(false)
    const [shake, setShake]             = useState(false)

    const q1 = row.CURRENT_TARGET_VALUE
    const { word, badge } = directionLabel(q1, targetType)

    // NT/null q1 → any valid target is an upgrade; NT target is always a drop
    const isUpgrade  = targetType !== 'NT' && (!q1 || q1 === 'NT' || TYPE_ORDER.indexOf(targetType) < TYPE_ORDER.indexOf(q1))
    const optionCodes = Array.isArray(options)
        ? options
        : (isUpgrade ? (options?.upgrade ?? []) : (options?.downgrade ?? []))

    const hasReasonCodes = optionCodes.length > 0
    const notesRequired  = reason.toLowerCase() === 'other'

    const name = exercise?.CUSTOMER_TYPE === 'HCP'
        ? [row.FIRST_NAME, row.MIDDLE_NAME, row.LAST_NAME, row.DEGREE ? `, ${row.DEGREE}` : ''].filter(Boolean).join(' ')
        : row.NAME

    // Trap focus inside modal on mount
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onClose])

    const triggerShake = () => {
        setShake(true)
        setTimeout(() => setShake(false), 400)
    }

    const handleSubmit = () => {
        const reasonOk = !hasReasonCodes || reason !== ''
        const notesOk  = !notesRequired  || notes.trim() !== ''
        setReasonError(!reasonOk)
        setNotesError(!notesOk)
        if (!reasonOk || !notesOk) { triggerShake(); return }
        onSubmit(targetType, { reason, notes })
    }

    // DIR read-only mode — show what was recorded and let the rep undo
    if (dirMode) {
        return (
            <div className={styles.backdrop} onClick={onClose}>
                <div className={styles.modal} onClick={e => e.stopPropagation()}>
                    <div className={styles.header}>
                        <span className={styles.title} style={{ color: '#6a1b9a' }}>DIR — {initialReason || 'Deceased / Inactive / Retired'}</span>
                        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
                    </div>
                    <div className={styles.directionRow}>
                        <span className={[styles.badge, styles.demoted].join(' ')}>Marked DIR</span>
                        <span className={styles.hcpName}>{name}</span>
                    </div>
                    <div className={styles.typeRow}>
                        <div className={styles.typeBlock}>
                            <div className={styles.typeLabel}>Proposed</div>
                            <TypeChip value={q1} size="lg" />
                        </div>
                        <div className={styles.arrow}>→</div>
                        <div className={styles.typeBlock}>
                            <div className={styles.typeLabel}>Final</div>
                            <span style={{ fontSize: '0.8rem', color: '#6a1b9a', fontWeight: 700 }}>NT (DIR)</span>
                        </div>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>Reason</label>
                        <input
                            className={styles.select}
                            value={initialReason || 'Deceased / Inactive / Retired'}
                            disabled
                            readOnly
                            style={{ background: 'color-mix(in srgb, #6a1b9a 6%, var(--surface))', color: '#6a1b9a', fontWeight: 600 }}
                        />
                    </div>
                    <div className={styles.actions}>
                        <button className={styles.cancelBtn} onClick={onClose}>Close</button>
                        {!lock && (
                            <button
                                className={styles.submitBtn}
                                style={{ background: '#2e7d32', borderColor: '#2e7d32' }}
                                onClick={onUndo}
                            >
                                Undo DIR — Restore {q1}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    // MOT read-only mode — show what was recorded and let the rep undo
    if (motMode) {
        return (
            <div className={styles.backdrop} onClick={onClose}>
                <div className={styles.modal} onClick={e => e.stopPropagation()}>
                    <div className={styles.header}>
                        <span className={styles.title} style={{ color: '#1565c0' }}>MOT — Moved Out of Territory</span>
                        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
                    </div>
                    <div className={styles.directionRow}>
                        <span className={[styles.badge, styles.changed].join(' ')}>Marked MOT</span>
                        <span className={styles.hcpName}>{name}</span>
                    </div>
                    <div className={styles.typeRow}>
                        <div className={styles.typeBlock}>
                            <div className={styles.typeLabel}>Proposed</div>
                            <TypeChip value={q1} size="lg" />
                        </div>
                        <div className={styles.arrow}>→</div>
                        <div className={styles.typeBlock}>
                            <div className={styles.typeLabel}>Final</div>
                            <TypeChip value={targetType} size="lg" />
                        </div>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>Reason</label>
                        <input
                            className={styles.select}
                            value="Moved Out of Territory"
                            disabled
                            readOnly
                            style={{ background: 'color-mix(in srgb, #1565c0 6%, var(--surface))', color: '#1565c0', fontWeight: 600 }}
                        />
                    </div>
                    <div className={styles.actions}>
                        <button className={styles.cancelBtn} onClick={onClose}>Close</button>
                        {!lock && (
                            <button
                                className={styles.submitBtn}
                                style={{ background: '#2e7d32', borderColor: '#2e7d32' }}
                                onClick={onUndoMOT}
                            >
                                Undo MOT
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.backdrop} onClick={onClose}>
            <div className={[styles.modal, shake ? styles.shake : ''].join(' ')} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className={styles.header}>
                    <span className={styles.title}>Edit Target</span>
                    <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
                </div>

                {/* Direction badge */}
                <div className={styles.directionRow}>
                    <span className={[styles.badge, styles[badge]].join(' ')}>
                        {word.charAt(0).toUpperCase() + word.slice(1)}
                    </span>
                    <span className={styles.hcpName}>{name}</span>
                </div>

                {/* Type transition */}
                <div className={styles.typeRow}>
                    <div className={styles.typeBlock}>
                        <div className={styles.typeLabel}>Proposed</div>
                        <TypeChip value={q1} size="lg" />
                    </div>
                    <div className={styles.arrow}>→</div>
                    <div className={styles.typeBlock}>
                        <div className={styles.typeLabel}>Final</div>
                        <TypeChip value={targetType} size="lg" />
                    </div>
                </div>

                {/* Reason */}
                {hasReasonCodes && (
                    <div className={styles.field}>
                        <label className={styles.label}>
                            Reason <span className={styles.required}>*</span>
                        </label>
                        <select
                            className={[styles.select, reasonError ? styles.inputError : ''].join(' ')}
                            value={reason}
                            onChange={e => { setReason(e.target.value); setReasonError(false) }}
                            disabled={lock}
                        >
                            <option value=""></option>
                            {optionCodes.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {reasonError && <span className={styles.errorMsg}>Please select a reason.</span>}
                    </div>
                )}

                {/* Notes */}
                <div className={styles.field}>
                    <label className={styles.label}>
                        Notes {notesRequired && <span className={styles.required}>*</span>}
                    </label>
                    <textarea
                        className={[styles.textarea, notesError ? styles.inputError : ''].join(' ')}
                        value={notes}
                        rows={3}
                        onChange={e => { setNotes(e.target.value); setNotesError(false) }}
                        disabled={lock}
                        placeholder={notesRequired ? 'Required when reason is "Other"' : 'Optional'}
                    />
                    {notesError && <span className={styles.errorMsg}>Notes are required when reason is "Other".</span>}
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
                    <button className={styles.submitBtn} onClick={handleSubmit} disabled={lock}>Submit</button>
                </div>
            </div>
        </div>
    )
}
